import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Editor } from '@tiptap/react'
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/src/renderer/components/ui/popover'
import { ProjectIcon } from '@/src/renderer/components/common/ProjectIcon'
import { IconPicker, type IconPickerValue } from '@/src/renderer/components/common/IconPicker'
import { ProjectTitleEditor } from './ProjectTitleEditor'
import { TitleGrip } from './TitleGrip'
import { TitleToolbar } from './TitleToolbar'
import { TitleMarkerLayer } from './TitleMarkerLayer'
import { PROJECT_TITLE_MARKER } from './titleTextPalette'
import './ProjectTitle.scss'

export interface ProjectHeaderTitleProps {
  /** Der Titel als HTML (ADR 0030). */
  value: string
  placeholder: string
  icon: string | null | undefined
  iconColor: string | null | undefined
  fieldRef: React.RefObject<HTMLElement | null>
  /** Die Zeile mit dem großen Icon — die Projektseite misst sie, um die Knöpfe daneben zu stellen. */
  iconRowRef?: React.Ref<HTMLDivElement>
  onValueChange: (html: string, plain: string) => void
  onSubmit: () => void
  onCancel: () => void
  onArrowDownAtEnd: () => void
  onFocus: () => void
  onIconChange: (next: IconPickerValue | null) => void
  /** Eine Formatierung aus dem Titelwerkzeug — sofort speichern. */
  onCommit: (html: string, plain: string) => void
}

/**
 * Der **Projekt-Kopf** (CONTEXT.md): das große Projekt-Icon (falls gesetzt),
 * darunter der gestaltbare Titel mit seinem **Titelgriff**, der links
 * hinaushängt und das **Titelwerkzeug** öffnet.
 *
 * - Ein Klick auf das große Icon öffnet den Icon-Wähler; der Griff daneben
 *   bleibt dann sichtbar und violett (entschieden 2026-09-25).
 * - Das Titelwerkzeug geht über den Griff auf — und von selbst, sobald im
 *   Titel etwas markiert ist (gewünscht 2026-09-26). Solange es steht, kann im
 *   Titel markiert werden — ein Klick in den Titel schließt es nicht.
 */
export const ProjectHeaderTitle: React.FC<ProjectHeaderTitleProps> = ({
  value,
  placeholder,
  icon,
  iconColor,
  fieldRef,
  iconRowRef,
  onValueChange,
  onSubmit,
  onCancel,
  onArrowDownAtEnd,
  onFocus,
  onIconChange,
  onCommit,
}) => {
  const { t } = useTranslation()
  const [editor, setEditor] = useState<Editor | null>(null)
  const [toolbarOpen, setToolbarOpen] = useState(false)
  const [iconPickerOpen, setIconPickerOpen] = useState(false)
  // Eine Farbe unter dem Zeiger im Icon-Wähler zeigt das Icon schon; gespeichert wird erst beim Klick (#246).
  const [iconPreview, setIconPreview] = useState<IconPickerValue | undefined>(undefined)
  const shownIcon = iconPreview?.icon ?? icon
  const shownIconColor = iconPreview ? iconPreview.iconColor : iconColor
  const handleIconPickerOpenChange = (open: boolean) => {
    setIconPickerOpen(open)
    if (!open) setIconPreview(undefined)
  }

  // **Markieren öffnet das Werkzeug.** Mit der Maus erst beim Loslassen —
  // sonst ginge es mitten im Ziehen auf. Hat die Markierung es geöffnet, geht
  // es mit ihr auch wieder zu; über den Griff geöffnet, bleibt es stehen.
  const openedBySelection = useRef(false)
  useEffect(() => {
    if (!editor) return
    let pointerDown = false
    const check = () => {
      if (pointerDown || !editor.isFocused) return
      if (!editor.state.selection.empty) {
        setToolbarOpen(open => {
          if (!open) openedBySelection.current = true
          return true
        })
      } else if (openedBySelection.current) {
        openedBySelection.current = false
        setToolbarOpen(false)
      }
    }
    const onPointerDown = () => { pointerDown = true }
    const onPointerUp = () => {
      if (!pointerDown) return
      pointerDown = false
      check()
    }
    const dom = editor.view.dom
    // **Daneben klicken hebt die Markierung auf** (gewünscht 2026-09-26).
    // Nicht beim Blur: Das Werkzeug formatiert ohne Fokus im Titel und
    // braucht die Markierung noch. Griff, Werkzeug und seine Menüs zählen
    // darum nicht als „daneben".
    const onDocumentPointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Element) || dom.contains(target)) return
      if (target.closest('.project-title__grip, [data-radix-popper-content-wrapper]')) return
      if (editor.state.selection.empty) return
      editor.chain().setMeta('addToHistory', false).setTextSelection(editor.state.selection.to).run()
      const domSelection = window.getSelection()
      if (domSelection?.anchorNode && dom.contains(domSelection.anchorNode)) domSelection.removeAllRanges()
    }
    dom.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('pointerup', onPointerUp)
    document.addEventListener('pointerdown', onDocumentPointerDown, true)
    editor.on('selectionUpdate', check)
    return () => {
      dom.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('pointerup', onPointerUp)
      document.removeEventListener('pointerdown', onDocumentPointerDown, true)
      editor.off('selectionUpdate', check)
    }
  }, [editor])

  const handleToolbarOpenChange = (open: boolean) => {
    openedBySelection.current = false
    setToolbarOpen(open)
  }

  // **Während einer Vorschau steht das Werkzeug still.** Eine überfahrene
  // Schrift ändert die Höhe des Titels; wanderte das Werkzeug mit, läge unter
  // dem Zeiger plötzlich eine andere Schrift, deren Vorschau es zurückschöbe —
  // es sprang hin und her (gemeldet 2026-09-26). Der Anker behält darum die
  // Höhe von vor der Vorschau; erst eine Wahl darf das Werkzeug verschieben.
  // Die Markierung weicht dabei, sonst verdeckte ihr Blau, was man gerade
  // ansieht (gewünscht 2026-09-26, wie auf der Pinnwand).
  const anchorRef = useRef<HTMLSpanElement>(null)
  const handlePreviewChange = (active: boolean) => {
    const anchor = anchorRef.current
    const line = anchor?.parentElement
    if (!anchor || !line) return
    if (active) {
      anchor.style.setProperty('--frozen-height', `${anchor.offsetHeight}px`)
      anchor.dataset.frozen = ''
      line.dataset.previewing = ''
    } else {
      delete anchor.dataset.frozen
      delete line.dataset.previewing
    }
  }

  const insideTitle = (target: EventTarget | null) =>
    target instanceof Node && !!fieldRef.current?.contains(target)

  return (
    <div className={`project-title${icon ? ' project-title--with-icon' : ''}`}>
      {icon && (
        <Popover open={iconPickerOpen} onOpenChange={handleIconPickerOpenChange}>
          <PopoverAnchor asChild>
            <div ref={iconRowRef} className={`project-title__icon-row${iconPickerOpen ? ' project-title__icon-row--open' : ''}`}>
              <PopoverTrigger asChild>
                <button type="button" className="project-title__icon" aria-label={t('projectTitle.iconGrip')}>
                  <ProjectIcon icon={shownIcon} iconColor={shownIconColor} size={64} />
                </button>
              </PopoverTrigger>
              <TitleGrip
                className="project-title__icon-grip"
                label={t('projectTitle.iconGrip')}
                open={iconPickerOpen}
                onClick={() => handleIconPickerOpenChange(!iconPickerOpen)}
              />
            </div>
          </PopoverAnchor>
          {/* Rechts neben Icon und Griff, oben bündig — darunter lag er über dem Titel (gewünscht 01.10.2026). */}
          <PopoverContent align="start" side="right" sideOffset={8}>
            <IconPicker
              value={icon}
              onValueChange={next => {
                onIconChange(next)
                // Eine Wahl lässt den Wähler offen (#246) — er geht beim Klick daneben zu. Entfernen nimmt
                // mit dem Icon auch seinen Anker.
                if (next === null) handleIconPickerOpenChange(false)
              }}
              onPreview={setIconPreview}
            />
          </PopoverContent>
        </Popover>
      )}

      <Popover open={toolbarOpen} onOpenChange={handleToolbarOpenChange}>
        <div className={`project-title__line${toolbarOpen ? ' project-title__line--open' : ''}`}>
            <TitleMarkerLayer editor={editor} spec={PROJECT_TITLE_MARKER} />
            <PopoverTrigger asChild>
              <TitleGrip
                className="project-title__grip"
                label={t('projectTitle.grip')}
                open={toolbarOpen}
                onMouseDown={e => e.preventDefault()}
              />
            </PopoverTrigger>
            <ProjectTitleEditor
              value={value}
              placeholder={placeholder}
              label={placeholder}
              fieldRef={fieldRef}
              onValueChange={onValueChange}
              onSubmit={() => { handleToolbarOpenChange(false); onSubmit() }}
              // Escape bei offenem Titelwerkzeug schließt nur das Werkzeug —
              // sonst nähme es die Gestaltung mit, die man gerade gewählt hat.
              onCancel={() => {
                if (toolbarOpen) handleToolbarOpenChange(false)
                else onCancel()
              }}
              onArrowDownAtEnd={onArrowDownAtEnd}
              onFocus={onFocus}
              onReady={setEditor}
            />
            {/*
              Das Werkzeug hängt unter dem Titelfeld, linksbündig mit dem Griff
              (Figma 3468:39971) — nicht am Griff selbst: Der steht mittig am
              Feld, und darunter läge das Werkzeug bei großer Schrift über dem
              Text (gemeldet 2026-09-26). Er steht HINTER dem Griff: Radix
              nimmt den zuletzt eingehängten Anker, und stünde der Griff
              zuletzt, bliebe das Werkzeug an dessen alter, beim Umbau
              ausgehängter Kopie hängen — und ginge oben links auf.
            */}
            <PopoverAnchor asChild>
              <span ref={anchorRef} className="project-title__toolbar-anchor" aria-hidden="true" />
            </PopoverAnchor>
        </div>
        <PopoverContent
          align="start"
          side="bottom"
          sideOffset={8}
          className="project-title__toolbar-popover"
          onOpenAutoFocus={e => e.preventDefault()}
          onCloseAutoFocus={e => e.preventDefault()}
          onInteractOutside={e => { if (insideTitle(e.target)) e.preventDefault() }}
        >
          <TitleToolbar editor={editor} icon={icon} onIconChange={onIconChange} onIconPreview={setIconPreview} onDone={() => handleToolbarOpenChange(false)} onCommit={onCommit} onPreviewChange={handlePreviewChange} />
        </PopoverContent>
      </Popover>
    </div>
  )
}
